import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import LocationForm from './LocationForm';
import useServerUrl from '../hooks/useServerUrl';
import './HomePage.css';

interface Location {
  id: string;
  name: string;
  description?: string;
  locationKey: string;
  userId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface DashboardStats {
  totalLocations: number;
  activeCameras: number;
  onlineStreams: number;
  totalUsers: number;
}

const HomePage: React.FC = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const SERVER_URL = useServerUrl(); // Runtime config
  
  // State management
  const [locations, setLocations] = useState<Location[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalLocations: 0,
    activeCameras: 0,
    onlineStreams: 0,
    totalUsers: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const locationsRef = useRef<Location[]>([]);
  const isFetchingRef = useRef(false);

  // Update ref when locations change
  useEffect(() => {
    locationsRef.current = locations;
  }, [locations]);

  const fetchData = useCallback(async () => {
    if (!token) {
      return;
    }

    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);
      
      // Fetch locations
      const locationsResponse = await axios.get(`${SERVER_URL}/api/locations`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000, // 10 second timeout
      });
      setLocations(locationsResponse.data);
      
      // Update stats based on fetched data
      setStats(prevStats => ({
        ...prevStats,
        totalLocations: locationsResponse.data.length,
        activeCameras: locationsResponse.data.filter((loc: Location) => loc.isActive).length * 4, // Estimate
        onlineStreams: locationsResponse.data.filter((loc: Location) => loc.isActive).length * 2, // Estimate
      }));
    } catch (err: any) {
      // Only set error if we don't have any locations yet
      if (locationsRef.current.length === 0) {
        const errorMessage = err.response?.data?.message || 
                           err.message || 
                           'Failed to fetch data';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [token]);

  // Fetch locations and stats
  useEffect(() => {
    fetchData();
  }, [token]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchData();
    }
  }, [refreshTrigger, token]);

  const handleLocationSelect = (location: Location) => {
    navigate(`/locations/${location.id}/video-livekit`);
  };

  const handleEditLocation = (location: Location) => {
    setEditingLocation(location);
    setShowLocationForm(true);
  };

  const handleAddLocation = () => {
    setEditingLocation(null);
    setShowLocationForm(true);
  };

  const handleLocationFormClose = () => {
    setShowLocationForm(false);
    setEditingLocation(null);
    setRefreshTrigger(prev => prev + 1);
  };

  const toggleKeyVisibility = (locationId: string) => {
    setVisibleKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(locationId)) {
        newSet.delete(locationId);
      } else {
        newSet.add(locationId);
      }
      return newSet;
    });
  };

  const copyLocationKey = async (locationKey: string, locationId: string) => {
    try {
      await navigator.clipboard.writeText(locationKey);
      setCopiedKey(locationId);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch (err) {
      console.error('Failed to copy location key:', err);
    }
  };

  const statCards = [
    {
      title: 'Total Locations',
      value: stats.totalLocations,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      color: 'from-blue-500 to-blue-600',
    },
    {
      title: 'Active Cameras',
      value: stats.activeCameras,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Online Streams',
      value: stats.onlineStreams,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      color: 'from-purple-500 to-purple-600',
    },
    {
      title: 'System Users',
      value: stats.totalUsers,
      icon: (
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
      color: 'from-orange-500 to-orange-600',
    },
  ];

  if (loading) {
    return (
      <div className={`min-h-screen ${
        theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className={`text-lg ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Loading your locations...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen ${
        theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className={`inline-flex items-center px-4 py-2 rounded-lg ${
              theme === 'dark' ? 'bg-red-900/50 text-red-400' : 'bg-red-50 text-red-600'
            }`}>
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="font-medium">{error}</p>
            </div>
            <button 
              onClick={fetchData}
              className={`mt-4 px-4 py-2 rounded-lg font-medium transition-colors ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      theme === 'dark' ? 'bg-slate-900' : 'bg-gray-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={`text-3xl font-bold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Welcome back, {user?.name?.split(' ')[0]}! 👋
              </h1>
              <p className={`mt-2 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Manage your Eagle Towers locations and monitor your camera streams.
              </p>
            </div>
            <div className={`hidden md:flex items-center space-x-3 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm">System Online</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <div
              key={index}
              className={`${
                theme === 'dark'
                  ? 'bg-slate-800 border-slate-700'
                  : 'bg-white border-gray-200'
              } border rounded-2xl p-6 transition-all duration-200 hover:shadow-lg transform hover:-translate-y-1`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {stat.title}
                  </p>
                  <p className={`text-2xl font-bold mt-1 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {stat.value}
                  </p>
                </div>
                <div className={`h-12 w-12 bg-gradient-to-r ${stat.color} rounded-xl flex items-center justify-center text-white shadow-lg`}>
                  {stat.icon}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Locations Section */}
        <div className={`${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        } border rounded-2xl p-6`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className={`text-xl font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Your Locations
              </h2>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                Select a location to view cameras or click edit to manage
              </p>
            </div>
            <button 
              onClick={handleAddLocation}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } hover:shadow-lg transform hover:-translate-y-1`}
            >
              ➕ Add Location
            </button>
          </div>
          
          {locations.length === 0 ? (
            <div className="text-center py-12">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${
                theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'
              } mb-4`}>
                <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className={`text-lg font-medium mb-2 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                No locations yet
              </h3>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              } mb-6`}>
                Create your first location to start managing cameras and streams.
              </p>
              <button 
                onClick={handleAddLocation}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  theme === 'dark'
                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                } hover:shadow-lg transform hover:-translate-y-1`}
              >
                Create Your First Location
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {locations.map((location, index) => (
                <div 
                  key={location.id} 
                  className={`location-card cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1 ${
                    theme === 'dark'
                      ? 'bg-slate-700 border-slate-600 hover:bg-slate-650'
                      : 'bg-gray-50 border-gray-200 hover:bg-white'
                  } border rounded-xl p-4`}
                  onClick={() => handleLocationSelect(location)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {location.name}
                      </h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        location.isActive
                          ? theme === 'dark'
                            ? 'bg-green-900/50 text-green-400'
                            : 'bg-green-100 text-green-800'
                          : theme === 'dark'
                            ? 'bg-gray-700 text-gray-400'
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {location.isActive ? '🟢 Active' : '🔴 Inactive'}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditLocation(location);
                      }}
                      className={`p-2 rounded-lg transition-colors ${
                        theme === 'dark'
                          ? 'text-gray-400 hover:text-white hover:bg-slate-600'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                      title="Edit location"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  </div>
                  
                  {location.description && (
                    <p className={`text-sm mb-3 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {location.description}
                    </p>
                  )}
                  
                  <div className="mb-3">
                    <label className={`text-xs font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Location Key:
                    </label>
                    <div className="flex items-center space-x-2 mt-1">
                      <code className={`flex-1 text-xs font-mono px-2 py-1 rounded overflow-hidden text-ellipsis ${
                        theme === 'dark' ? 'bg-slate-600 text-gray-300' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {visibleKeys.has(location.id) ? location.locationKey : '••••••••••••••••••••••••••••••••••••'}
                      </code>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleKeyVisibility(location.id);
                        }}
                        className={`p-1.5 rounded transition-colors ${
                          theme === 'dark'
                            ? 'text-gray-400 hover:text-white hover:bg-slate-600'
                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                        title={visibleKeys.has(location.id) ? 'Hide Key' : 'Show Key'}
                      >
                        {visibleKeys.has(location.id) ? (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          copyLocationKey(location.locationKey, location.id);
                        }}
                        className={`p-1.5 rounded transition-all ${
                          copiedKey === location.id
                            ? theme === 'dark'
                              ? 'bg-green-900/50 text-green-400'
                              : 'bg-green-100 text-green-600'
                            : theme === 'dark'
                              ? 'text-gray-400 hover:text-white hover:bg-slate-600'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                        title={copiedKey === location.id ? 'Copied!' : 'Copy Key'}
                      >
                        {copiedKey === location.id ? (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div className={`text-xs ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    Created: {new Date(location.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Location Form Modal */}
      {showLocationForm && (
        <LocationForm
          location={editingLocation}
          onClose={handleLocationFormClose}
          onSuccess={handleLocationFormClose}
        />
      )}
    </div>
  );
};

export default HomePage;
